import { LessonProblem } from '@yandex-int/k-common/typings';
import isEmpty from 'lodash/isEmpty';
import { Help, Text } from 'platform-components';
import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import StudentLink from 'common.components/results/__student-link/results__student-link';
import TableBody from 'common.components/results/__table-body/results__table-body';
import TableCell from 'common.components/results/__table-cell/results__table-cell';
import TableHead from 'common.components/results/__table-head/results__table-head';
import TableRow from 'common.components/results/__table-row/results__table-row';
import Table from 'common.components/results/__table/results__table';
import * as selectors from 'common.components/results/selectors';
import { DictationMarkerSpecificData, StudentsResult } from 'common.components/results/typings';
import { getCellTime, getCellValue, helpProps } from 'common.components/results/utils';
import { getTeamIdFromPath } from 'utils/path';

import './results__dictation.scss';

interface OwnProps {
    onStudentNameClick: (studentId: number) => void;
    problemLink: LessonProblem;
    shouldShowMistakes: boolean;
}

interface HOCProps extends WrappedComponentProps {}

interface StoreProps {
    studentsResult: Array<StudentsResult>;
}

interface Props extends OwnProps, StoreProps, HOCProps {}

const DictationResult = (props: Props) => {
    const { onStudentNameClick, problemLink, studentsResult, intl, shouldShowMistakes } = props;

    return (
        <div className="results__dictation">
            <div className="results__dictation-title">
                <Text id="lessonView.results.dictation.title" size={Text.SIZE.XL} weight={Text.WEIGHT.M} />
            </div>
            <div className="results__dictation-description">
                <Text id="lessonView.results.dictation.description" type={Text.TYPE.MARKDOWN} />
            </div>
            <Table>
                <TableHead>
                    <TableCell type={TableCell.TYPE.COUNTER}>
                        <Text id="lessonView.results.counter" />
                    </TableCell>
                    <TableCell type={TableCell.TYPE.NAME}>
                        <Text id="lessonView.results.studentName" />
                    </TableCell>
                    <TableCell type={TableCell.TYPE.OVERALL}>
                        <Text id="lessonView.results.dictation.orfoMistakes" size={Text.SIZE.S} />
                    </TableCell>
                    <TableCell type={TableCell.TYPE.OVERALL}>
                        <Text id="lessonView.results.dictation.punctMistakes" size={Text.SIZE.S} />
                    </TableCell>
                    <TableCell type={TableCell.TYPE.OVERALL}>
                        <Text id="lessonView.results.dictation.typos" size={Text.SIZE.S} />
                    </TableCell>
                    <TableCell type={TableCell.TYPE.OVERALL}>
                        <Text id="lessonView.results.time" size={Text.SIZE.S} />
                        <Help {...helpProps}>
                            <Text id="lessonView.results.help" type={Text.TYPE.MARKDOWN} />
                        </Help>
                    </TableCell>
                </TableHead>
                <TableBody>
                    {/* eslint-disable-next-line complexity */}
                    {studentsResult.map((studentResult, index) => {
                        const problemResult = studentResult.result.problems[problemLink.id];
                        const markerSpecificData: DictationMarkerSpecificData =
                            problemResult.marker_specific_data && !isEmpty(problemResult.marker_specific_data)
                                ? Object.values(problemResult.marker_specific_data)[0].summary
                                : null;
                        const orfoMistakes = markerSpecificData?.orfo_mistakes || 0;
                        const punctMistakes = markerSpecificData?.punct_mistakes || 0;
                        const typos = markerSpecificData?.typos || 0;

                        return (
                            <TableRow key={`student-result-${studentResult.studentId}`}>
                                <TableCell type={TableCell.TYPE.COUNTER}>
                                    <Text>{index + 1}</Text>
                                </TableCell>
                                <TableCell type={TableCell.TYPE.NAME}>
                                    <StudentLink
                                        onClick={onStudentNameClick}
                                        studentId={studentResult.studentId}
                                        studentName={studentResult.studentName}
                                    />
                                </TableCell>

                                <TableCell type={TableCell.TYPE.OVERALL}>
                                    <Text>{getCellValue(shouldShowMistakes ? orfoMistakes : null, intl)}</Text>
                                </TableCell>
                                <TableCell type={TableCell.TYPE.OVERALL}>
                                    <Text>{getCellValue(shouldShowMistakes ? punctMistakes : null, intl)}</Text>
                                </TableCell>
                                <TableCell type={TableCell.TYPE.OVERALL}>
                                    <Text>{getCellValue(shouldShowMistakes ? typos : null, intl)}</Text>
                                </TableCell>

                                <TableCell type={TableCell.TYPE.OVERALL}>
                                    <Text>
                                        {getCellValue(
                                            shouldShowMistakes ? getCellTime(problemResult.time) : null,
                                            intl
                                        )}
                                    </Text>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

const mapStateToProps = (storeState: any): StoreProps => {
    const classId = getTeamIdFromPath();

    return {
        studentsResult: selectors.getStudentsResult(storeState, { classId }),
    };
};

const wrapped = injectIntl(connect(mapStateToProps)(DictationResult));
wrapped.displayName = 'DictationResult';

export default wrapped;
